import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Megaphone, Upload, Loader2, Crown, CheckCircle2, Clock, XCircle,
  CreditCard, ArrowLeft, Rocket, Download, ExternalLink, Play,
} from "lucide-react";
import { adsApi } from "@/api/mist";
import { compressImage, previewUrl } from "@/lib/imageCompress";
import { useSession } from "@/context/SessionContext";

const GOLD = "#FFCC00";

const STATUS_META = {
  awaiting_payment: { label: "Awaiting payment", color: "#FBBF24", Icon: CreditCard },
  pending_review: { label: "In review", color: "#60A5FA", Icon: Clock },
  approved: { label: "Live", color: "#34D399", Icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "#F87171", Icon: XCircle },
};

export default function AdvertisePortal() {
  const navigate = useNavigate();
  const { user } = useSession();
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({ title: "", advertiser: "", target_url: "", package_id: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payingId, setPayingId] = useState(null);

  const filePreview = useMemo(() => (file ? previewUrl(file) : ""), [file]);
  useEffect(() => () => { if (filePreview) URL.revokeObjectURL(filePreview); }, [filePreview]);

  const loadMine = useCallback(() => {
    adsApi.mine().then((d) => setMine(d.slots || [])).catch(() => setMine([]));
  }, []);

  useEffect(() => {
    adsApi.packages().then((d) => {
      setPackages(d.packages || []);
      setForm((f) => ({ ...f, package_id: (d.packages || [])[0]?.id || "" }));
    }).catch(() => setPackages([]));
    if (user) loadMine();
  }, [user, loadMine]);

  // Stripe return handling.
  useEffect(() => {
    if (params.get("cancelled")) {
      toast.message("Checkout cancelled — your ad is saved as unpaid.");
      window.history.replaceState({}, "", "/advertise");
      return;
    }
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0, live = true;
    const poll = async () => {
      try {
        const d = await adsApi.checkoutStatus(sid);
        if (d.paid) {
          toast.success("Payment received — your ad is now in founder review!");
          window.history.replaceState({}, "", "/advertise");
          loadMine();
          return;
        }
      } catch { /* keep trying */ }
      tries += 1;
      if (live && tries < 8) setTimeout(poll, 1600);
    };
    poll();
    return () => { live = false; };
  }, [params, loadMine]);

  const startCheckout = async (slotId) => {
    setPayingId(slotId);
    try {
      const r = await adsApi.checkout(slotId, window.location.origin);
      if (r.checkout_url) window.location.assign(r.checkout_url);
      else toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setPayingId(null);
    }
  };

  const submit = async () => {
    if (!form.title.trim() || !form.advertiser.trim() || !form.package_id || !file) {
      toast.error("Title, advertiser, a package and an image are required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("advertiser", form.advertiser.trim());
      fd.append("package_id", form.package_id);
      fd.append("target_url", form.target_url.trim());
      fd.append("scope", "game");
      fd.append("file", await compressImage(file));
      const r = await adsApi.submit(fd);
      setForm({ title: "", advertiser: "", target_url: "", package_id: packages[0]?.id || "" });
      setFile(null);
      loadMine();
      toast.success("Ad created — redirecting to secure payment…");
      await startCheckout(r.slot.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  if (!user) {
    return (
      <div data-testid="advertise-signin-gate" className="flex h-full w-full flex-col items-center justify-center gap-4 bg-[#0b1020] p-8 text-center">
        <Megaphone size={48} style={{ color: GOLD }} />
        <h1 className="font-serif text-2xl text-white">Advertise on Misteck</h1>
        <p className="max-w-[280px] font-mono text-[11px] leading-relaxed text-white/50">
          Sign in to book a flat-rate ad slot that rotates inside our games.
        </p>
        <button onClick={() => navigate("/")} data-testid="advertise-gate-signin"
          className="mt-2 rounded-full px-6 py-3 font-mono text-[11px] font-black uppercase tracking-[0.18em] text-black"
          style={{ backgroundColor: GOLD }}>
          Sign in to continue
        </button>
      </div>
    );
  }

  const selected = packages.find((p) => p.id === form.package_id);

  return (
    <div data-testid="advertise-portal" className="h-full w-full overflow-y-auto bg-[#0b1020] text-white">
      <div className="mx-auto max-w-[560px] p-4 sm:p-6">
        <button onClick={() => navigate("/")} data-testid="advertise-back"
          className="mb-4 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-gold">
          <ArrowLeft size={14} /> Back to Misteck
        </button>

        <div className="mb-5 flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-2xl border-2" style={{ borderColor: GOLD }}>
            <Megaphone size={22} style={{ color: GOLD }} />
          </div>
          <div>
            <h1 className="font-serif text-3xl leading-none text-white">Advertise with us</h1>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/40">
              Flat-rate slots · rotate inside Ball Snipe & Tetminer
            </p>
          </div>
        </div>

        {/* Packages */}
        <p className="mb-2 font-mono text-[10px] font-black uppercase tracking-widest text-gold">1 · Pick a package</p>
        <div className="mb-5 grid grid-cols-1 gap-2" data-testid="advertise-packages">
          {packages.map((p) => {
            const active = form.package_id === p.id;
            const apex = p.tier === "apex";
            return (
              <button key={p.id} data-testid={`advertise-pkg-${p.id}`} onClick={() => setForm({ ...form, package_id: p.id })}
                className={`flex items-center justify-between rounded-xl border-2 p-3 text-left transition-colors ${active ? "border-gold bg-gold/10" : "border-white/15 bg-black/40"}`}>
                <div className="flex items-center gap-2">
                  {apex && <Crown size={14} style={{ color: GOLD }} />}
                  <div>
                    <p className="font-mono text-[12px] font-black text-white">{p.label}</p>
                    <p className="font-mono text-[9px] text-white/40">{p.days} days{apex ? " · 2× rotation weight" : ""}</p>
                  </div>
                </div>
                <span className="font-mono text-base font-black" style={{ color: active ? GOLD : "#fff" }}>${p.price_aud.toFixed(0)}</span>
              </button>
            );
          })}
        </div>

        {/* Form */}
        <p className="mb-2 font-mono text-[10px] font-black uppercase tracking-widest text-gold">2 · Your ad</p>
        <div className="mb-3 space-y-2 rounded-xl border-2 border-white/15 bg-black/40 p-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ad title / headline" data-testid="advertise-title"
            className="h-10 w-full rounded-lg border-2 border-white/15 bg-black px-3 font-mono text-[12px] text-white focus:border-gold focus:outline-none" />
          <input value={form.advertiser} onChange={(e) => setForm({ ...form, advertiser: e.target.value })} placeholder="Business / advertiser name" data-testid="advertise-advertiser"
            className="h-10 w-full rounded-lg border-2 border-white/15 bg-black px-3 font-mono text-[12px] text-white focus:border-gold focus:outline-none" />
          <input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="Landing URL (https://…)" data-testid="advertise-url"
            className="h-10 w-full rounded-lg border-2 border-white/15 bg-black px-3 font-mono text-[12px] text-white focus:border-gold focus:outline-none" />
          <label data-testid="advertise-file-label" className="flex h-10 cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-white/25 bg-black px-3">
            <Upload size={14} className="text-white/60" />
            <span className="truncate font-mono text-[11px] text-white/60">{file ? file.name : "Upload banner image or MP4 (≤30s)"}</span>
            <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden" data-testid="advertise-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          {file && (
            <div className="overflow-hidden rounded-lg border-2 border-white/10 bg-black/40" data-testid="advertise-file-preview">
              {file.type?.startsWith("video/") ? (
                <video src={filePreview} muted playsInline className="w-full object-cover max-h-40" />
              ) : (
                <img src={filePreview} alt="preview" className="w-full object-cover max-h-40" />
              )}
            </div>
          )}
        </div>

        <button onClick={submit} disabled={busy} data-testid="advertise-submit"
          className="mb-8 flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-mono text-[11px] font-black uppercase tracking-widest text-black disabled:opacity-50"
          style={{ backgroundColor: GOLD }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
          {selected ? `Pay $${selected.price_aud.toFixed(0)} & submit` : "Create & pay"}
        </button>

        {/* My ads */}
        <p className="mb-2 font-mono text-[10px] font-black uppercase tracking-widest text-gold">Your ads</p>
        <div className="space-y-2" data-testid="advertise-my-ads">
          {mine.length === 0 ? (
            <p className="py-3 text-center font-mono text-[10px] text-white/40">No ads yet — book your first slot above.</p>
          ) : mine.map((s) => {
            const meta = STATUS_META[s.status] || STATUS_META.pending_review;
            const Icon = meta.Icon;
            return (
              <div key={s.id} data-testid={`advertise-ad-${s.id}`} className="rounded-xl border-2 border-white/15 bg-black/40 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-[12px] font-black text-white">{s.title}</p>
                    <p className="font-mono text-[9px] text-white/40">{s.advertiser} · ${Number(s.price_aud || 0).toFixed(0)} · {s.days}d</p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[9px] font-black uppercase"
                    style={{ borderColor: `${meta.color}66`, color: meta.color }}>
                    <Icon size={11} /> {meta.label}
                  </span>
                </div>
                {/* Media preview + view/download */}
                <div className="mt-2 overflow-hidden rounded-lg border-2 border-white/10 bg-black/40">
                  {s.media_kind === "video" ? (
                    <video src={adsApi.mediaUrl(s.id)} controls muted playsInline className="w-full object-cover max-h-40" data-testid={`advertise-media-${s.id}`} />
                  ) : (
                    <img src={adsApi.mediaUrl(s.id)} alt={s.title} className="w-full object-cover max-h-40" data-testid={`advertise-media-${s.id}`} />
                  )}
                </div>
                <div className="mt-2 flex gap-2">
                  <a href={adsApi.mediaUrl(s.id)} target="_blank" rel="noreferrer" data-testid={`advertise-view-${s.id}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border-2 border-white/20 bg-white/5 py-2 font-mono text-[10px] font-black uppercase tracking-widest text-white/80">
                    {s.media_kind === "video" ? <Play size={12} /> : <ExternalLink size={12} />} View
                  </a>
                  <a href={adsApi.mediaUrl(s.id, true)} data-testid={`advertise-download-${s.id}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border-2 border-white/20 bg-white/5 py-2 font-mono text-[10px] font-black uppercase tracking-widest text-white/80">
                    <Download size={12} /> Download
                  </a>
                </div>
                {s.status === "rejected" && s.reject_reason && (
                  <p className="mt-1.5 font-mono text-[9px] text-red-300/80">Reason: {s.reject_reason}</p>
                )}
                {s.status === "awaiting_payment" && (
                  <button onClick={() => startCheckout(s.id)} disabled={payingId === s.id} data-testid={`advertise-pay-${s.id}`}
                    className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-gold bg-gold/10 py-2 font-mono text-[10px] font-black uppercase tracking-widest text-gold disabled:opacity-50">
                    {payingId === s.id ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />} Pay ${Number(s.price_aud || 0).toFixed(0)} now
                  </button>
                )}
                {s.status === "approved" && (
                  <p className="mt-1.5 flex items-center gap-1 font-mono text-[9px] text-emerald-300/80"><Rocket size={10} /> Rotating live in the games</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
